import { useCallback } from 'react';

import { MediaPlans } from '@/api/MediaPlans';
import { Orders } from '@/api/Orders';
import { Services } from '@/api/Services';
import { Tasks } from '@/api/Tasks';
import {
  AttachmentResponse,
  MediaPlanResponse,
  OrderResponse,
  ServiceTaskResponse,
} from '@/api/data-contracts';
import { ContentType } from '@/api/http-client';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

const useArtworkService = () => {
  const mediaPlansApi = useUploadProgressService(MediaPlans);
  const ordersApi = useUploadProgressService(Orders);
  const servicesApi = useUploadProgressService(Services);
  const tasksApi = useUploadProgressService(Tasks);

  const downloadArtworkForOrder = async (planId: string, bookingId: string) => {
    const { data } = await ordersApi.downloadOrderArtworkDocuments(planId, bookingId, {
      format: 'blob',
    });
    return data as Blob;
  };

  const downloadArtworkDocumentForOrder = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string | number,
      signal?: AbortSignal,
    ) => {
      const { data } = await ordersApi.downloadOrderArtworkDocument(
        orderId,
        bookingId,
        documentId.toString(),
        { format: 'blob', signal },
      );
      return data as Blob;
    },
    [ordersApi],
  );

  const downloadArtworkForMediaPlan = async (orderId: string, bookingId: string) => {
    const { data } = await mediaPlansApi.downloadMediaPlanArtworkDocument(orderId, bookingId, {
      format: 'blob',
    });
    return data as Blob;
  };

  const downloadArtworkDocumentForMediaPlan = useCallback(
    async (
      planId: string,
      bookingId: string,
      documentId: string | number,
      signal?: AbortSignal,
    ) => {
      const { data } = await mediaPlansApi.downloadMediaPlanArtworkDocuments(
        planId,
        bookingId,
        documentId.toString(),
        { format: 'blob', signal },
      );
      return data as Blob;
    },
    [mediaPlansApi],
  );

  const uploadArtworkForOrder = async (
    orderId: string,
    bookingId: string,
    artwork: File[],
    operationId?: string,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.uploadOrderArtwork(
      orderId,
      bookingId,
      // @ts-ignore
      { artwork },
      operationId ? { operationId } : {},
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  };

  const uploadArtworkForMediaPlan = async (
    mediaPlanId: string,
    bookingId: string,
    artwork: File[],
  ) => {
    const { data } = await mediaPlansApi.uploadMediaPlanArtwork(
      mediaPlanId,
      bookingId,
      // @ts-ignore
      { artwork },
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return data;
  };

  const uploadArtworkPreviewForOrder = async (
    orderId: string,
    bookingId: string,
    files: File[],
    operationId?: string,
  ) => {
    const form = new FormData();
    files.forEach((f) => form.append('artwork', f));

    const { data } = await ordersApi.uploadOrderArtworkPreview(
      orderId,
      bookingId,
      // @ts-ignore
      form,
      operationId ? { operationId } : {},
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    return data;
  };

  const downloadArtworkPreviewForOrder = async (
    orderId: string,
    bookingId: string,
  ): Promise<Blob> => {
    const { data } = await ordersApi.downloadOrderArtworkPreview(orderId, bookingId, {
      format: 'blob',
    });

    return data as Blob;
  };

  const downloadArtworkPreviewDocumentForOrder = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string | number,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const { data } = await ordersApi.downloadOrderArtworkPreviewDocument(
        orderId,
        bookingId,
        documentId.toString(),
        { format: 'blob', signal },
      );
      return data as Blob;
    },
    [ordersApi],
  );

  const deleteArtworkPreviewDocumentForOrder = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string | number,
    ): Promise<OrderResponse> => {
      const { data } = await ordersApi.deleteOrderArtworkPreview(
        orderId,
        bookingId,
        documentId.toString(),
      );
      return data;
    },
    [ordersApi],
  );

  const deleteArtworkDocumentForOrder = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string | number,
    ): Promise<OrderResponse> => {
      const { data } = await ordersApi.deleteOrderArtwork(
        orderId,
        bookingId,
        documentId.toString(),
      );
      return data;
    },
    [ordersApi],
  );

  const uploadArtworkPreviewForMediaPlan = async (
    planId: string,
    bookingId: string,
    files: File[],
  ): Promise<MediaPlanResponse> => {
    const form = new FormData();
    files.forEach((f) => form.append('artwork', f));

    const { data } = await mediaPlansApi.uploadMediaPlanArtworkPreview(
      planId,
      bookingId,
      // @ts-ignore
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    return data;
  };

  const downloadArtworkPreviewForMediaPlan = async (
    planId: string,
    bookingId: string,
  ): Promise<Blob> => {
    const { data } = await mediaPlansApi.downloadMediaPlanArtworkPreviewDocuments(
      planId,
      bookingId,
      {
        format: 'blob',
      },
    );

    return data as Blob;
  };

  const downloadArtworkPreviewDocumentForMediaPlan = useCallback(
    async (
      planId: string,
      bookingId: string,
      documentId: string | number,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const { data } = await mediaPlansApi.downloadMediaPlanArtworkPreviewDocument(
        planId,
        bookingId,
        documentId.toString(),
        { format: 'blob', signal },
      );

      return data as Blob;
    },
    [mediaPlansApi],
  );

  const deleteArtworkPreviewDocumentForMediaPlan = useCallback(
    async (
      planId: string,
      bookingId: string,
      documentId: string | number,
    ): Promise<MediaPlanResponse> => {
      const { data } = await mediaPlansApi.deleteMediaPlanArtworkPreview(
        planId,
        bookingId,
        documentId.toString(),
      );

      return data;
    },
    [mediaPlansApi],
  );

  // artwork methods for service tasks
  const uploadTaskArtwork = useCallback(
    async (taskId: string, files: File[]): Promise<ServiceTaskResponse> => {
      const { data } = await servicesApi.uploadArtwork(taskId, { files });
      return data;
    },
    [servicesApi],
  );

  const downloadTaskArtwork = useCallback(
    async (taskId: string, documentId: string | number, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await servicesApi.downloadServiceTaskArtwork(taskId, documentId.toString(), {
        format: 'blob',
        signal,
      });
      return data as Blob;
    },
    [servicesApi],
  );

  const deleteTaskArtwork = useCallback(
    async (taskId: string, documentId: string | number): Promise<ServiceTaskResponse> => {
      const { data } = await servicesApi.deleteServiceTaskArtwork(taskId, documentId.toString());
      return data;
    },
    [servicesApi],
  );

  const uploadTaskArtworkPreview = useCallback(
    async (taskId: string, files: File[]): Promise<ServiceTaskResponse> => {
      const { data } = await servicesApi.uploadArtworkPreview(taskId, { files });
      return data;
    },
    [servicesApi],
  );

  const downloadTaskArtworkPreview = useCallback(
    async (taskId: string, documentId: string | number, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await servicesApi.downloadServiceTaskArtworkPreview(
        taskId,
        documentId.toString(),
        { format: 'blob', signal },
      );
      return data as Blob;
    },
    [servicesApi],
  );

  const deleteTaskArtworkPreview = useCallback(
    async (taskId: string, documentId: string | number): Promise<ServiceTaskResponse> => {
      const { data } = await servicesApi.deleteServiceTaskArtworkPreview(
        taskId,
        documentId.toString(),
      );
      return data;
    },
    [servicesApi],
  );

  const downloadArtworkFileForTask = useCallback(
    async (taskId: string, documentId: string | number, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await servicesApi.downloadArtworkFileForServiceTask(
        taskId,
        documentId.toString(),
        { format: 'blob', signal },
      );
      return data as Blob;
    },
    [servicesApi],
  );

  const downloadTaskAttachmentVersion = useCallback(
    async (taskId: string, attachmentId: string, versionId: number): Promise<Blob> => {
      const { data } = await tasksApi.downloadAttachmentVersion1(taskId, attachmentId, versionId, {
        format: 'blob',
      });
      return data as Blob;
    },
    [tasksApi],
  );

  const uploadArtworkPreviewNewVersionForMediaPlan = useCallback(
    async (
      planId: string,
      attachmentId: string,
      formData: FormData,
    ): Promise<AttachmentResponse> => {
      const { data } = await mediaPlansApi.updateNewVersion2(
        planId,
        'artWorkPreview',
        attachmentId,
        {} as any,
        { body: formData, type: ContentType.FormData } as any,
      );
      return data;
    },
    [mediaPlansApi],
  );

  const deleteAttachmentVersionForMediaPlan = useCallback(
    async (
      planId: string,
      attachmentId: string,
      versionId: number,
    ): Promise<AttachmentResponse> => {
      const { data } = await mediaPlansApi.deleteVersion2(planId, attachmentId, versionId);
      return data;
    },
    [mediaPlansApi],
  );

  const setAttachmentActiveVersionForMediaPlan = useCallback(
    async (
      planId: string,
      attachmentId: string,
      versionId: number,
    ): Promise<AttachmentResponse> => {
      const { data } = await mediaPlansApi.setActiveVersion2(planId, attachmentId, versionId);
      return data;
    },
    [mediaPlansApi],
  );

  const downloadAttachmentVersionForMediaPlan = useCallback(
    async (planId: string, attachmentId: string, versionId: number): Promise<Blob> => {
      const { data } = await mediaPlansApi.downloadAttachmentVersion3(
        planId,
        attachmentId,
        versionId,
        { format: 'blob' },
      );
      return data as Blob;
    },
    [mediaPlansApi],
  );

  const downloadAttachmentVersionForOrder = useCallback(
    async (orderId: string, attachmentId: string, versionId: number): Promise<Blob> => {
      const { data } = await ordersApi.downloadAttachmentVersion2(
        orderId,
        attachmentId,
        versionId,
        { format: 'blob' },
      );
      return data as Blob;
    },
    [ordersApi],
  );

  const deleteArtworkDocumentForMediaPlan = useCallback(
    async (
      planId: string,
      bookingId: string,
      documentId: string | number,
    ): Promise<MediaPlanResponse> => {
      const { data } = await mediaPlansApi.deleteMediaPlanArtworkDocument(
        planId,
        bookingId,
        documentId.toString(),
        {
          headers: { 'Content-Type': 'application/json' },
          // @ts-ignore
          body: {},
        },
      );
      return data;
    },
    [mediaPlansApi],
  );

  return {
    downloadArtworkForOrder,
    uploadArtworkForOrder,
    downloadArtworkDocumentForOrder,
    uploadArtworkPreviewForOrder,
    downloadArtworkPreviewForOrder,
    downloadArtworkPreviewDocumentForOrder,
    deleteArtworkPreviewDocumentForOrder,
    deleteArtworkDocumentForOrder,

    downloadArtworkForMediaPlan,
    uploadArtworkForMediaPlan,
    downloadArtworkDocumentForMediaPlan,
    uploadArtworkPreviewForMediaPlan,
    downloadArtworkPreviewForMediaPlan,
    downloadArtworkPreviewDocumentForMediaPlan,
    deleteArtworkPreviewDocumentForMediaPlan,

    uploadTaskArtwork,
    downloadTaskArtwork,
    deleteTaskArtwork,
    uploadTaskArtworkPreview,
    downloadTaskArtworkPreview,
    deleteTaskArtworkPreview,
    downloadArtworkFileForTask,
    downloadTaskAttachmentVersion,
    uploadArtworkPreviewNewVersionForMediaPlan,
    deleteAttachmentVersionForMediaPlan,
    setAttachmentActiveVersionForMediaPlan,
    downloadAttachmentVersionForMediaPlan,
    downloadAttachmentVersionForOrder,
    deleteArtworkDocumentForMediaPlan,
  };
};

export default useArtworkService;
